import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { Link } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ArrowDown, ArrowUp, LoaderCircle, Search, X } from "lucide-react";
import { type FormEvent, lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  type CatalogFilter,
  type CatalogSort,
  type CatalogTitle,
  errorMessage,
  type FacetCount,
  fetchCatalog,
  fetchFacets,
} from "@/lib/api";
import { MEDIA_STATUS, mediaStatusLabel } from "@/lib/media";
import { codelistQuery } from "@/lib/products";

// The dialog and its parts load with the first catalogue view, not with the app.
const CatalogExport = lazy(() =>
  import("@/components/export-dialog").then((m) => ({ default: m.CatalogExport })),
);

/** Height of a table row in pixels; all rows have one line. */
const ROW_HEIGHT = 40;
/** Wait this long after the last key before searching. */
const SEARCH_DELAY_MS = 250;
/** Load the next page this many rows before the end. */
const PREFETCH_ROWS = 30;

const VALIDATION: Record<
  CatalogTitle["validation_status"],
  { label: string; variant: "success" | "secondary" | "destructive" }
> = {
  ok: { label: "OK", variant: "success" },
  warning: { label: "Warnung", variant: "secondary" },
  error: { label: "Fehler", variant: "destructive" },
};

const SORTABLE: CatalogSort[] = [
  "title",
  "contributors",
  "isbn13",
  "publishing_date",
  "updated_at",
];

/** `2026-03-01` → `01.03.2026`, `2026-03` → `03/2026`, otherwise unchanged. */
function formatDate(date: string | null): string {
  if (!date) return "";
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (day) return `${day[3]}.${day[2]}.${day[1]}`;
  const month = /^(\d{4})-(\d{2})$/.exec(date);
  if (month) return `${month[2]}/${month[1]}`;
  return date;
}

const DATE = /^\d{4}(-\d{2}(-\d{2})?)?$/;
const count = (n: number) => n.toLocaleString("de-DE");

/** Code → English EDItEUR text of a code list; empty while loading. */
function useCodes(list: number): Map<string, string> {
  const query = useQuery(codelistQuery(list));
  return useMemo(
    () => new Map(query.data?.codes.map((c) => [c.value, c.description]) ?? []),
    [query.data],
  );
}

type Props = { filter: CatalogFilter; onFilter: (next: CatalogFilter) => void };

/** `/` (UI-01–03, B8): all titles of the publisher with search, filters and sorting. */
export function CatalogPage({ filter, onFilter }: Props) {
  const forms = useCodes(150);
  const statuses = useCodes(64);
  const facets = useQuery({ queryKey: ["facets"], queryFn: fetchFacets, staleTime: 60_000 });
  const catalog = useInfiniteQuery({
    queryKey: ["catalog", filter],
    queryFn: ({ pageParam }) => fetchCatalog(filter, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    // The old rows stay until the new ones are there: no flicker while typing.
    placeholderData: keepPreviousData,
  });
  const titles = useMemo(() => catalog.data?.pages.flatMap((p) => p.titles) ?? [], [catalog.data]);
  const total = catalog.data?.pages[0]?.total ?? null;
  const filtered = Object.entries(filter).some(
    ([key, value]) =>
      key !== "sort" && key !== "dir" && (Array.isArray(value) ? value.length : value),
  );
  const set = (patch: Partial<CatalogFilter>) => onFilter({ ...filter, ...patch });

  return (
    <div className="mx-auto grid max-w-7xl gap-4 px-4 py-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">Katalog</h1>
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {/* The old rows stay while searching, their count does not. */}
          {total === null || catalog.isPlaceholderData
            ? "Wird geladen …"
            : filtered && facets.data
              ? `${count(total)} von ${count(facets.data.total)} Titeln`
              : `${count(total)} Titel`}
        </p>
      </div>

      <SearchBox value={filter.q ?? ""} onSearch={(q) => set({ q: q || undefined })} />

      <div className="flex flex-wrap items-center gap-2">
        <ListFilter
          label="Prüfung"
          values={facets.data?.validation_status}
          selected={filter.validation}
          labelOf={(v) => VALIDATION[v as CatalogTitle["validation_status"]]?.label ?? v}
          onChange={(validation) => set({ validation })}
        />
        <ListFilter
          label="Status"
          values={facets.data?.publishing_status}
          selected={filter.status}
          labelOf={(v) => codeLabel(v, statuses)}
          onChange={(status) => set({ status })}
        />
        <ListFilter
          label="Produktform"
          values={facets.data?.product_form}
          selected={filter.form}
          labelOf={(v) => codeLabel(v, forms)}
          onChange={(form) => set({ form })}
        />
        <ListFilter
          label="Verlag"
          values={facets.data?.publisher}
          selected={filter.publisher}
          labelOf={(v) => v}
          onChange={(publisher) => set({ publisher })}
        />
        <ListFilter
          label="Medien"
          values={facets.data?.media_status}
          selected={filter.media}
          labelOf={mediaStatusLabel}
          onChange={(media) => set({ media })}
        />
        <DateFilter from={filter.from} to={filter.to} onChange={(from, to) => set({ from, to })} />
        <div className="ml-auto">
          <Suspense fallback={null}>
            <CatalogExport filter={filter} total={catalog.isPlaceholderData ? null : total} />
          </Suspense>
        </div>
        {filtered && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onFilter({ sort: filter.sort, dir: filter.dir })}
          >
            Alle Filter entfernen
          </Button>
        )}
      </div>

      <Chips
        filter={filter}
        labels={{
          validation: (v) => VALIDATION[v as CatalogTitle["validation_status"]]?.label ?? v,
          status: (v) => codeLabel(v, statuses),
          form: (v) => codeLabel(v, forms),
          media: mediaStatusLabel,
        }}
        onFilter={onFilter}
      />

      {catalog.isError && <Alert variant="destructive">{errorMessage(catalog.error)}</Alert>}

      {facets.data?.total === 0 && !filtered ? (
        <Alert>Noch keine Titel. Über „Import“ eine ONIX-Datei hochladen.</Alert>
      ) : (
        <CatalogTable
          titles={titles}
          total={total}
          loading={catalog.isPending || (catalog.isFetching && !catalog.isFetchingNextPage)}
          forms={forms}
          statuses={statuses}
          sort={filter.sort ?? "title"}
          desc={filter.dir === "desc"}
          onSort={(sort, desc) => set({ sort, dir: desc ? "desc" : undefined })}
          hasMore={catalog.hasNextPage}
          loadMore={() => {
            if (!catalog.isFetchingNextPage) void catalog.fetchNextPage();
          }}
        />
      )}
    </div>
  );
}

function codeLabel(code: string, codes: Map<string, string>): string {
  const text = codes.get(code);
  return text ? `${code} – ${text}` : code;
}

/** Search field; sends the text a moment after the last key. */
function SearchBox({ value, onSearch }: { value: string; onSearch: (q: string) => void }) {
  const [text, setText] = useState(value);
  // Back and forward in the browser change the search from outside.
  const [shown, setShown] = useState(value);
  if (value !== shown) {
    setShown(value);
    setText(value);
  }
  useEffect(() => {
    if (text.trim() === value.trim()) return;
    const timer = setTimeout(() => onSearch(text.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [text, value, onSearch]);

  return (
    <div className="relative">
      <Search
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <Input
        type="search"
        aria-label="Suche"
        placeholder="ISBN, Titel, Autor, Reihe …"
        className="pl-9"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
    </div>
  );
}

/** Closes a drop-down (`<details>`) on a click outside it and on Escape. */
function useCloseOutside() {
  const ref = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    const close = (event: Event) => {
      const details = ref.current;
      if (!details?.open) return;
      if (
        event instanceof KeyboardEvent
          ? event.key === "Escape"
          : !details.contains(event.target as Node)
      ) {
        details.open = false;
      }
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, []);
  return ref;
}

type ListFilterProps = {
  label: string;
  values: FacetCount[] | undefined;
  selected: string[] | undefined;
  labelOf: (value: string) => string;
  onChange: (values: string[] | undefined) => void;
};

/** A drop-down with a check box per value (with the number of titles). */
function ListFilter({ label, values, selected = [], labelOf, onChange }: ListFilterProps) {
  const ref = useCloseOutside();
  const toggle = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next.length ? next : undefined);
  };
  return (
    <details ref={ref} className="group relative">
      <summary className="flex h-8 cursor-pointer list-none items-center gap-1 rounded-md border px-3 text-sm hover:bg-accent">
        {label}
        {selected.length > 0 && <Badge variant="secondary">{selected.length}</Badge>}
      </summary>
      <div className="absolute z-20 mt-1 max-h-80 w-72 overflow-auto rounded-md border bg-background p-1 shadow-md">
        {values === undefined ? (
          <p className="p-2 text-sm text-muted-foreground">Wird geladen …</p>
        ) : values.length === 0 ? (
          <p className="p-2 text-sm text-muted-foreground">Keine Werte</p>
        ) : (
          values.map(({ value, count: n }) => (
            <label
              key={value}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent"
            >
              <input
                type="checkbox"
                className="size-4 accent-primary"
                checked={selected.includes(value)}
                onChange={() => toggle(value)}
              />
              <span className="min-w-0 flex-1 truncate" title={labelOf(value)}>
                {labelOf(value)}
              </span>
              <span className="text-xs text-muted-foreground">{count(n)}</span>
            </label>
          ))
        )}
      </div>
    </details>
  );
}

type DateFilterProps = {
  from: string | undefined;
  to: string | undefined;
  onChange: (from: string | undefined, to: string | undefined) => void;
};

/** Publication date from/to; a year or month takes all of it. */
function DateFilter({ from, to, onChange }: DateFilterProps) {
  const ref = useCloseOutside();
  const [start, setStart] = useState(from ?? "");
  const [end, setEnd] = useState(to ?? "");
  const [shown, setShown] = useState([from, to]);
  if (shown[0] !== from || shown[1] !== to) {
    setShown([from, to]);
    setStart(from ?? "");
    setEnd(to ?? "");
  }
  const invalid = [start, end].some((d) => d.trim() !== "" && !DATE.test(d.trim()));
  const apply = (e: FormEvent) => {
    e.preventDefault();
    if (invalid) return;
    onChange(start.trim() || undefined, end.trim() || undefined);
    if (ref.current) ref.current.open = false;
  };
  return (
    <details ref={ref} className="relative">
      <summary className="flex h-8 cursor-pointer list-none items-center gap-1 rounded-md border px-3 text-sm hover:bg-accent">
        Erscheinen
        {(from || to) && <Badge variant="secondary">1</Badge>}
      </summary>
      <form
        onSubmit={apply}
        className="absolute z-20 mt-1 grid w-72 gap-2 rounded-md border bg-background p-3 shadow-md"
      >
        <label className="grid gap-1 text-sm">
          von
          <Input value={start} placeholder="2026-01" onChange={(e) => setStart(e.target.value)} />
        </label>
        <label className="grid gap-1 text-sm">
          bis
          <Input value={end} placeholder="2026-12" onChange={(e) => setEnd(e.target.value)} />
        </label>
        {invalid && (
          <p className="text-sm text-destructive">Datum wie 2026, 2026-03 oder 2026-03-01.</p>
        )}
        <Button type="submit" size="sm" disabled={invalid}>
          Übernehmen
        </Button>
      </form>
    </details>
  );
}

type ChipsProps = {
  filter: CatalogFilter;
  labels: Record<"validation" | "status" | "form" | "media", (value: string) => string>;
  onFilter: (next: CatalogFilter) => void;
};

const CHIP_NAMES = {
  validation: "Prüfung",
  status: "Status",
  form: "Produktform",
  publisher: "Verlag",
  media: "Medien",
} as const;

/** The active filters, each removable on its own. */
function Chips({ filter, labels, onFilter }: ChipsProps) {
  const chips: { text: string; remove: () => void }[] = [];
  for (const key of ["validation", "status", "form", "publisher", "media"] as const) {
    for (const value of filter[key] ?? []) {
      const text = `${CHIP_NAMES[key]}: ${key === "publisher" ? value : labels[key](value)}`;
      const rest = (filter[key] ?? []).filter((v) => v !== value);
      chips.push({
        text,
        remove: () => onFilter({ ...filter, [key]: rest.length ? rest : undefined }),
      });
    }
  }
  if (filter.from || filter.to) {
    chips.push({
      text: `Erscheinen: ${filter.from ? formatDate(filter.from) : "…"} – ${filter.to ? formatDate(filter.to) : "…"}`,
      remove: () => onFilter({ ...filter, from: undefined, to: undefined }),
    });
  }
  if (filter.q) {
    chips.push({
      text: `Suche: ${filter.q}`,
      remove: () => onFilter({ ...filter, q: undefined }),
    });
  }
  if (chips.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-2" aria-label="Aktive Filter">
      {chips.map((chip) => (
        <li key={chip.text}>
          <Badge variant="outline" className="gap-1 py-1 pr-1 text-sm font-normal">
            {chip.text}
            <button
              type="button"
              className="rounded p-0.5 hover:bg-accent"
              aria-label={`Filter „${chip.text}“ entfernen`}
              onClick={chip.remove}
            >
              <X className="size-3" aria-hidden />
            </button>
          </Badge>
        </li>
      ))}
    </ul>
  );
}

type TableProps = {
  titles: CatalogTitle[];
  total: number | null;
  loading: boolean;
  forms: Map<string, string>;
  statuses: Map<string, string>;
  sort: CatalogSort;
  desc: boolean;
  onSort: (sort: CatalogSort, desc: boolean) => void;
  hasMore: boolean;
  loadMore: () => void;
};

/**
 * The titles as a table that only renders the visible rows (UI-01);
 * scrolling near the end loads the next page.
 */
function CatalogTable(props: TableProps) {
  const { titles, total, loading, forms, statuses, sort, desc, onSort, hasMore, loadMore } = props;
  const columns = useMemo<ColumnDef<CatalogTitle>[]>(
    () => [
      {
        id: "isbn13",
        header: "ISBN",
        size: 140,
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.isbn13}</span>,
      },
      {
        id: "title",
        header: "Titel",
        size: 300,
        cell: ({ row }) => {
          const { id, title, subtitle, record_reference } = row.original;
          return (
            <Link
              to="/titel/$id"
              params={{ id: String(id) }}
              title={subtitle ? `${title ?? ""} – ${subtitle}` : (title ?? "")}
              className="underline-offset-2 hover:underline"
            >
              {title ?? <span className="text-muted-foreground italic">{record_reference}</span>}
              {subtitle && <span className="text-muted-foreground"> – {subtitle}</span>}
            </Link>
          );
        },
      },
      {
        id: "contributors",
        header: "Autor",
        size: 180,
        cell: ({ row }) => row.original.contributors,
      },
      {
        id: "series",
        header: "Reihe",
        size: 140,
        cell: ({ row }) => row.original.series,
      },
      {
        id: "publisher",
        header: "Verlag",
        size: 150,
        cell: ({ row }) => row.original.publisher,
      },
      {
        id: "product_form",
        header: "Form",
        size: 64,
        cell: ({ row }) => {
          const form = row.original.product_form;
          return form && <span title={forms.get(form)}>{form}</span>;
        },
      },
      {
        id: "publishing_date",
        header: "Erscheinen",
        size: 100,
        cell: ({ row }) => formatDate(row.original.publishing_date),
      },
      {
        id: "publishing_status",
        header: "Status",
        size: 140,
        cell: ({ row }) => {
          const status = row.original.publishing_status;
          return status && (statuses.get(status) ?? status);
        },
      },
      {
        id: "validation_status",
        header: "Prüfung",
        size: 90,
        cell: ({ row }) => {
          const v = VALIDATION[row.original.validation_status];
          return <Badge variant={v.variant}>{v.label}</Badge>;
        },
      },
      {
        id: "media_status",
        header: "Medien",
        size: 100,
        cell: ({ row }) => {
          const status = row.original.media_status;
          if (!status) return <span className="text-muted-foreground">–</span>;
          const m = MEDIA_STATUS[status];
          return <Badge variant={m.variant}>{m.label}</Badge>;
        },
      },
      {
        id: "updated_at",
        header: "Geändert",
        size: 100,
        cell: ({ row }) => new Date(row.original.updated_at).toLocaleDateString("de-DE"),
      },
    ],
    [forms, statuses],
  );
  const sorting: SortingState = [{ id: sort, desc }];
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table, as documented
  const table = useReactTable({
    data: titles,
    columns,
    state: { sorting },
    // Sorted on the server; the header buttons change the address.
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id),
  });
  const rows = table.getRowModel().rows;

  const scroller = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scroller.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  });
  const items = virtualizer.getVirtualItems();
  const lastIndex = items.at(-1)?.index ?? -1;
  useEffect(() => {
    if (hasMore && lastIndex >= rows.length - PREFETCH_ROWS) loadMore();
  }, [hasMore, lastIndex, rows.length, loadMore]);
  // A new search or order starts at the top.
  useEffect(() => {
    scroller.current?.scrollTo?.({ top: 0 });
  }, [sort, desc]);

  const top = items[0]?.start ?? 0;
  const bottom = virtualizer.getTotalSize() - (items.at(-1)?.end ?? 0);
  const width = table.getVisibleLeafColumns().length;

  return (
    <div
      ref={scroller}
      className="relative h-[calc(100svh-17rem)] min-h-80 overflow-auto rounded-md border"
    >
      <table
        className="w-full min-w-[1500px] table-fixed border-collapse text-sm"
        aria-rowcount={total === null ? undefined : total + 1}
        aria-busy={loading}
      >
        <colgroup>
          {table.getVisibleLeafColumns().map((column) => (
            <col key={column.id} style={{ width: column.getSize() }} />
          ))}
        </colgroup>
        <thead className="sticky top-0 z-10 bg-background shadow-[0_1px_0_var(--color-border)]">
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id}>
              {group.headers.map((header) => {
                const id = header.column.id as CatalogSort;
                const sortable = SORTABLE.includes(id);
                const active = sortable && id === sort;
                return (
                  <th
                    key={header.id}
                    scope="col"
                    className="h-10 px-3 text-left font-medium text-muted-foreground"
                    aria-sort={active ? (desc ? "descending" : "ascending") : undefined}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 hover:text-foreground"
                        onClick={() => onSort(id, active ? !desc : false)}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {active &&
                          (desc ? (
                            <ArrowDown className="size-3" aria-hidden />
                          ) : (
                            <ArrowUp className="size-3" aria-hidden />
                          ))}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {top > 0 && (
            <tr aria-hidden>
              <td colSpan={width} style={{ height: top }} />
            </tr>
          )}
          {items.map((item) => {
            const row = rows[item.index]!;
            return (
              <tr
                key={row.id}
                aria-rowindex={item.index + 2}
                className="border-b hover:bg-accent/50"
                style={{ height: ROW_HEIGHT }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="truncate px-3">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
          {bottom > 0 && (
            <tr aria-hidden>
              <td colSpan={width} style={{ height: bottom }} />
            </tr>
          )}
        </tbody>
      </table>
      {rows.length === 0 && !loading && (
        <p className="p-6 text-center text-sm text-muted-foreground">Keine Titel gefunden.</p>
      )}
      {loading && rows.length === 0 && (
        <p className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
          <LoaderCircle className="animate-spin" aria-hidden /> Wird geladen …
        </p>
      )}
    </div>
  );
}
